import React, { useState, useEffect } from "react";
import { toast } from "sonner";
import { Utensils, ShoppingBag, ArrowRight, CheckCircle2, X } from "lucide-react";
import { motion, AnimatePresence } from "motion/react";
import { useAuth } from "../../../lib/auth";
import { supabase } from "../../../lib/supabase";
import { trackEvent } from "../../../lib/analytics";

interface Choice {
  key: "FOOD_VENDOR" | "STORE_MANAGER";
  title: string;
  subtitle: string;
  desc: string;
  icon: React.ReactNode;
  gradient: string;
  borderHover: string;
  textGlow: string;
  preview: React.ReactNode;
}

export function BusinessTypeModal({ open, onClose }: { open: boolean; onClose: () => void }) {
  const { profile, refreshProfile } = useAuth();
  const [loading, setLoading] = useState(false);
  const [hoveredType, setHoveredType] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      trackEvent("business_type_modal_viewed");
    }
  }, [open]);

  if (!open || !profile) return null;

  const submit = async (businessType: string) => {
    setLoading(true);
    trackEvent("business_type_selected", { type: businessType });
    try {
      const { error } = await supabase
        .from("profiles")
        .update({ business_type: businessType })
        .eq("id", profile.id);

      if (error) throw new Error(error.message);

      trackEvent("business_type_completed", { type: businessType });
      toast.success("Welcome aboard! Personalizing your experience...", {
        duration: 3000,
        icon: <CheckCircle2 className="w-5 h-5 text-emerald-500" />,
      });
      await refreshProfile?.();
      onClose();
    } catch (e: any) {
      toast.error("Could not save business type: " + (e.message || e));
    } finally {
      setLoading(false);
    }
  };

  const choices: Choice[] = [
    {
      key: "FOOD_VENDOR",
      title: "Food Vendor",
      subtitle: "Menus & Scheduled Deliveries",
      desc: "Perfect for restaurants, caterers, meal preppers, and dark kitchens selling portions, food trays, or timed menus.",
      icon: <Utensils className="w-5 h-5 text-amber-500" />,
      gradient: "from-amber-500/10 via-rose-500/5 to-transparent",
      borderHover: "group-hover:border-amber-500/50 focus-within:border-amber-500/50",
      textGlow: "group-hover:shadow-[0_0_20px_rgba(245,158,11,0.15)]",
      preview: (
        <div className="flex items-center gap-2 p-2 bg-white/70 backdrop-blur-sm rounded-xl border border-amber-500/10 text-left text-xs shadow-inner">
          <div className="w-7 h-7 rounded-lg bg-gradient-to-br from-amber-500/20 to-rose-500/20 flex items-center justify-center text-xs">🍲</div>
          <div className="flex-1 min-w-0">
            <p className="font-semibold text-slate-800 truncate text-[10px] leading-tight">Spiced Jollof Rice</p>
            <p className="text-[8.5px] text-slate-500 font-medium">Daily Delivery • 12PM - 2PM</p>
          </div>
          <span className="font-bold text-slate-700 text-[10px] shrink-0">₦4,500</span>
        </div>
      ),
    },
    {
      key: "STORE_MANAGER",
      title: "Store Manager",
      subtitle: "Boutique Catalog & SKUs",
      desc: "Built for apparel, accessories, beauty, groceries, and digital product stores requiring grids, stock counts, and variants.",
      icon: <ShoppingBag className="w-5 h-5 text-emerald-500" />,
      gradient: "from-emerald-500/10 via-cyan-500/5 to-transparent",
      borderHover: "group-hover:border-emerald-500/50 focus-within:border-emerald-500/50",
      textGlow: "group-hover:shadow-[0_0_20px_rgba(16,185,129,0.15)]",
      preview: (
        <div className="flex items-center gap-2 p-2 bg-white/70 backdrop-blur-sm rounded-xl border border-emerald-500/10 text-left text-xs shadow-inner">
          <div className="w-7 h-7 rounded-lg bg-gradient-to-br from-emerald-500/20 to-cyan-500/20 flex items-center justify-center text-xs">👜</div>
          <div className="flex-1 min-w-0">
            <p className="font-semibold text-slate-800 truncate text-[10px] leading-tight">Crossbody Bag</p>
            <p className="text-[8.5px] text-amber-600 font-bold">3 Left • Tracked SKU</p>
          </div>
          <span className="font-bold text-slate-700 text-[10px] shrink-0">₦28,000</span>
        </div>
      ),
    },
  ];

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/60 backdrop-blur-md p-4 overflow-y-auto">
      <motion.div
        initial={{ opacity: 0, scale: 0.95, y: 10 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
        className="relative bg-white/90 backdrop-blur-xl rounded-3xl w-full max-w-xl p-5 md:p-6 shadow-[0_25px_50px_-12px_rgba(0,0,0,0.25)] border border-white/50 text-slate-800 overflow-hidden"
      >
        {/* Skip button in the top-right corner */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 p-2 rounded-full text-slate-400 hover:text-slate-600 hover:bg-slate-100/80 transition-colors z-20"
          title="Skip for now"
        >
          <X className="w-4 h-4" />
        </button>

        {/* Abstract floating circles for aesthetic glow */}
        <div className="absolute -top-32 -left-32 w-64 h-64 rounded-full bg-amber-500/10 blur-3xl pointer-events-none" />
        <div className="absolute -bottom-32 -right-32 w-64 h-64 rounded-full bg-emerald-500/10 blur-3xl pointer-events-none" />

        <div className="relative z-10 text-center max-w-lg mx-auto mb-5">
          <div className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full bg-indigo-500/10 text-indigo-600 text-[10px] font-semibold tracking-wide uppercase mb-2">
            ✨ Onboarding Tailoring
          </div>
          <h3 className="text-xl md:text-2xl font-extrabold tracking-tight text-slate-950">
            Tell us about your business
          </h3>
          <p className="mt-1 text-xs md:text-sm text-slate-500 leading-relaxed">
            Select the configuration that best describes how you operate. We will dynamically adapt your order system, dashboard analytics, and storefront visual layout.
          </p>
        </div>

        <div className="relative z-10 grid md:grid-cols-2 gap-4">
          {choices.map((c) => {
            const isHovered = hoveredType === c.key;
            return (
              <motion.button
                key={c.key}
                onClick={() => submit(c.key)}
                disabled={loading}
                onMouseEnter={() => setHoveredType(c.key)}
                onMouseLeave={() => setHoveredType(null)}
                whileHover={{ scale: 1.01, y: -2 }}
                whileTap={{ scale: 0.99 }}
                className={`group relative flex flex-col justify-between rounded-2xl border border-slate-200/80 bg-gradient-to-br ${c.gradient} p-4 md:p-4.5 text-left transition-all ${c.borderHover} ${c.textGlow} disabled:opacity-50`}
              >
                <div className="w-full">
                  <div className="flex items-center gap-2.5 mb-2.5">
                    <div className="p-2 rounded-xl bg-white shadow-sm border border-slate-100">
                      {c.icon}
                    </div>
                    <div>
                      <h4 className="font-bold text-sm text-slate-950 leading-tight">
                        {c.title}
                      </h4>
                      <span className="text-[9px] font-medium text-slate-400 tracking-wide uppercase">
                        {c.subtitle}
                      </span>
                    </div>
                  </div>

                  <p className="text-[11px] md:text-[12px] text-slate-500 leading-relaxed mb-4">
                    {c.desc}
                  </p>
                </div>

                <div className="w-full mt-auto">
                  <div className="mb-4 overflow-hidden transition-all duration-300">
                    {c.preview}
                  </div>

                  <div className="flex items-center justify-between text-[11px] font-bold text-slate-800 pt-2 border-t border-slate-100/60 group-hover:text-slate-950">
                    <span className="tracking-wide">Select This Flow</span>
                    <span className="inline-flex items-center justify-center w-5.5 h-5.5 rounded-full bg-slate-100 group-hover:bg-slate-900 group-hover:text-white transition-colors duration-200">
                      <ArrowRight className="w-3 h-3" />
                    </span>
                  </div>
                </div>
              </motion.button>
            );
          })}
        </div>

        <div className="mt-5 flex flex-col items-center gap-2">
          <div className="text-center text-[10px] text-slate-400 font-medium">
            🔒 Enforced standard workflow selection. You can update this once in settings later.
          </div>
          <button
            onClick={onClose}
            className="text-[10px] font-black uppercase tracking-widest text-slate-500 hover:text-slate-700 bg-slate-100 hover:bg-slate-200/80 px-4 py-1.5 rounded-full transition-all duration-200 shadow-sm border border-slate-200"
          >
            Skip For Now
          </button>
        </div>
      </motion.div>
    </div>
  );
}

